import { Injectable } from "@angular/core";
import { Actions, Effect, ofType } from "@ngrx/effects";
import { of } from "rxjs";
import { withLatestFrom, switchMap, filter, map, share } from "rxjs/operators";
import { DialogsService } from "../../services/dialogs.service";
import { ApplicationMessagesAction } from "./application-message.action";
import { IApplicationMessagePayload } from "./application-messages-payload.interface";
import { ApplicationMessageType } from "./application-message-type.enum";
import { ToastrService } from "ngx-toastr";
import { Store } from "@ngrx/store";
import { AppState } from "../../interfaces/app-state.interface";
import { TrackingActions } from "../tracking/tracking.action";

@Injectable()
export class ApplicationMessagesEffects {
  @Effect({ dispatch: false }) public genericError$ = this.actions$.pipe(
    ofType(ApplicationMessagesAction.genericError),
    withLatestFrom(this.store.select(state => state.applicationMessages)),
    filter(([action, messages]) => !messages.lastMessage),
    map(([action, messages]: any) => {
      const payload: any = action.payload;
      if (payload) {
        if (payload.message) {
          this.checkError(payload);
        } else if (Array.isArray(payload.value)) {
          this.checkError(payload.value[0]);
        }
      } else {
        this.checkError();
      }
      this.store.dispatch({
        type: ApplicationMessagesAction.genericErrorStarted
      });
    }),
    share()
  );

  @Effect() public warningToast$ = this.actions$.pipe(
    ofType(ApplicationMessagesAction.toastWarningMessage),
    switchMap((action: any) => {
      this.checkError(action.payload, ApplicationMessageType.Warning);
      return of({
        type: ApplicationMessagesAction.toastWarningMessageCompleted,
        payload: action.payload
      });
    }),
    share()
  );

  @Effect() public infoToast$ = this.actions$.pipe(
    ofType(ApplicationMessagesAction.toastInfoMessage),
    switchMap((action: any) => {
      this.checkError(action.payload, ApplicationMessageType.Info);
      return of({
        type: ApplicationMessagesAction.toastInfoMessageCompleted,
        payload: action.payload
      });
    }),
    share()
  );

  @Effect() public successToast$ = this.actions$.pipe(
    ofType(ApplicationMessagesAction.toastSuccessMessage),
    switchMap((action: any) => {
      this.checkError(action.payload, ApplicationMessageType.Success);
      return of({
        type: ApplicationMessagesAction.toastSuccessMessageCompleted,
        payload: action.payload
      });
    }),
    share()
  );

  @Effect() public errorToast$ = this.actions$.pipe(
    ofType(ApplicationMessagesAction.toastError),
    switchMap((action: any) => {
      this.checkError(action.payload, ApplicationMessageType.Error);
      return of({
        type: ApplicationMessagesAction.toastErrorCompleted,
        payload: action.payload
      });
    }),
    share()
  );

  private toastrSettings = {
    timeOut: 7000,
    progressBar: true,
    positionClass: "toast-bottom-center",
    toastClass: "toast--default font-small--medium font-light-color"
  };

  constructor(
    private actions$: Actions<ApplicationMessagesAction>,
    private store: Store<AppState>,
    private dialogsService: DialogsService,
    private toastrService: ToastrService
  ) {}

  /**
   * Exibe um error na aplicação de acordo com a sua classificação
   * @param payload dados para exibição do erro
   * @param type tipo do erro a ser tratado
   */
  private checkError(
    payload?: IApplicationMessagePayload,
    type: ApplicationMessageType = ApplicationMessageType.GenericError
  ) {
    if (
      ![ApplicationMessageType.Success, ApplicationMessageType.Info].includes(
        type
      )
    ) {
      this.store.dispatch({
        type: TrackingActions.trackFormEvent,
        payload: {
          type: "Error",
          event: "Erro_servidor"
        }
      });
    }

    switch (type) {
      case ApplicationMessageType.GenericError:
        let message: string = `Infelizmente, tivemos um erro de conexão ou de servidor.
                Por favor, tente novamente.`;
        let title: string = "Erro de servidor ou conexão";
        if (payload && payload.message) {
          message = payload.message;
          title = payload.title;
        }
        this.toastrService
          .error(message, title, this.toastrSettings)
          .onHidden.subscribe(() => this.completeGenericError());
        break;

      case ApplicationMessageType.Error:
        this.toastrService
          .error(payload.message, payload.title, this.toastrSettings)
          .onHidden.subscribe(() => this.completeGenericError());
        break;

      case ApplicationMessageType.Success:
        this.toastrService
          .success(payload.message, payload.title, this.toastrSettings)
          .onHidden.subscribe(() => this.completeGenericError());
        break;

      case ApplicationMessageType.Info:
        this.toastrService
          .info(payload.message, payload.title, this.toastrSettings)
          .onHidden.subscribe(() => this.completeGenericError());
        break;

      case ApplicationMessageType.Warning:
        this.toastrService
          .warning(payload.message, payload.title, this.toastrSettings)
          .onHidden.subscribe(() => this.completeGenericError());
        break;
      default:
        this.toastrService
          .warning(payload.message, payload.title, this.toastrSettings)
          .onHidden.subscribe(() => this.completeGenericError());
        break;
    }
  }

  private completeGenericError() {
    this.store.dispatch({
      type: ApplicationMessagesAction.genericErrorCompleted
    });
  }
}
